import { AdmCompany } from "@custom-types/base-types";
import { EntityCellParams } from "@components/common/data-grid/adm-data-grid-types";
import { ReactNode } from "react";
import {
  DataGridCell,
  DataGridDateCell,
  getEntity,
} from "@components/common/data-grid/adm-data-grid-utils";
import { FaroCoreApiTypes, FaroCoreApiUtils } from "@stellar/core-api-client";
import { COMPANY_TYPE_CONFIG } from "@pages/workspace-profile/workspace-constants";
import { Chip, grey, useToast } from "@flat-ui";
import { isAdmUserCompany } from "@utils/type-guards";
import {
  GeneralTableEvents,
  MainPageEvents,
} from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { TableType } from "@utils/track-event/event-types";
import { DataGridOptionCell } from "@components/common/data-grid/data-grid-option-cell";
import { useGenerateRoutes } from "@router/router-helper";
import { UiUtils } from "@stellar/utils";

type AllWorkspaceTypes = AdmCompany | FaroCoreApiTypes.AdmUserCompany;

/** Return workspace name cell in data-grid */
export function WorkspaceNameCell(
  params: EntityCellParams<AllWorkspaceTypes>
): ReactNode {
  const entity = getEntity(params);
  return (
    <DataGridCell
      content={entity.name}
      tooltip={`${entity.name} (${entity.id})`}
    />
  );
}

/** Return workspace project count cell in data-grid */
export function WorkspaceProjectCountCell(
  params: EntityCellParams<AllWorkspaceTypes>
): ReactNode {
  return <DataGridCell content={getEntity(params).projectCount} />;
}

/** Return workspace member count cell in data-grid */
export function WorkspaceMemberCountCell(
  params: EntityCellParams<AllWorkspaceTypes>
): ReactNode {
  return <DataGridCell content={getEntity(params).memberCount} />;
}

/** Return workspace createdAt cell in data-grid */
export function WorkspaceCreatedAtCell(
  params: EntityCellParams<AllWorkspaceTypes>
): ReactNode {
  return <DataGridDateCell date={getEntity(params).createdAt} />;
}

/** Return workspace member role cell in data-grid */
export function WorkspaceMemberRoleCell(
  params: EntityCellParams<AllWorkspaceTypes>
): ReactNode {
  const entity = getEntity(params);
  if (isAdmUserCompany(entity)) {
    return (
      <DataGridCell
        content={FaroCoreApiUtils.Workspace.formatUserWorkspaceRole(
          entity.role
        )}
      />
    );
  }

  return <DataGridCell content={UiUtils.DASH} />;
}

/** Return workspace type cell in data-grid */
export function WorkspaceTypeRoleCell(
  params: EntityCellParams<AllWorkspaceTypes>
): ReactNode {
  const entity = getEntity(params);
  const currentConfig =
    entity.type && entity.type in COMPANY_TYPE_CONFIG
      ? COMPANY_TYPE_CONFIG[entity.type]
      : COMPANY_TYPE_CONFIG.unspecified;

  return (
    <Chip
      label={currentConfig.label}
      color={grey[0]}
      backgroundColor={currentConfig.color}
      textTransform="none"
      sx={{
        borderRadius: "6px",
        fontSize: "14px",
        height: "32px",
        p: "0 10px",
      }}
    />
  );
}

/** Return workspace option cell in data-grid */
export function WorkspaceOptionCell(
  params: EntityCellParams<AllWorkspaceTypes>,
  location: TableType
): ReactNode {
  const { trackEvent } = useTrackEvent();
  const entity = getEntity(params);
  const { generateCompanyRoute } = useGenerateRoutes();
  const { openToast } = useToast();

  return (
    <DataGridOptionCell
      item={entity}
      menuItems={[
        {
          label: "Open workspace profile",
          onSelect: () => {
            trackEvent({
              name: MainPageEvents.openItem,
              props: {
                target: "COMPANY",
                location,
              },
            });
          },
          getHrefUrl: (entity) =>
            generateCompanyRoute({
              companyId: entity.id,
            }).pathname,
        },
        {
          label: "Copy workspace ID",
          onSelect: async () => {
            trackEvent({
              name: GeneralTableEvents.copyRowIdToClipboard,
              props: {
                location,
              },
            });
            await navigator.clipboard.writeText(entity.id);
            openToast({
              title: "Workspace ID copied to clipboard!",
            });
          },
        },
      ]}
    />
  );
}
